import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/auth-hook';
import { useI18n } from '@/contexts/i18n-hook';
import { AssinaturaManager } from '@/components/apreciacao/assinatura-manager';
import { apiService } from '@/services/api';
import { Loader2, User, Camera } from 'lucide-react';

function urlParaExibicaoFoto(url: string | undefined | null): string | undefined {
  if (!url) return undefined;
  if (url.startsWith('data:') || url.startsWith('http') || url.startsWith('/')) return url;
  return url;
}

export function PerfilPage() {
  const { user, refreshUser } = useAuth();
  const { t } = useI18n();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fotoUrl, setFotoUrl] = useState<string>('');
  const [assinaturaUrl, setAssinaturaUrl] = useState<string | undefined>(undefined);
  const [assinaturaDigital, setAssinaturaDigital] = useState<string | undefined>(undefined);
  const [cargo, setCargo] = useState('');
  const [crea, setCrea] = useState('');
  const [descricao, setDescricao] = useState('');

  useEffect(() => {
    const load = async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const res = await apiService.getMe();
        if (res.success && res.data) {
          const u = res.data as any;
          setFotoUrl(u.fotoUrl ?? '');
          setAssinaturaUrl(u.assinaturaUrl ?? undefined);
          setAssinaturaDigital(u.assinaturaDigital ?? undefined);
          setCargo(u.cargo ?? '');
          setCrea(u.crea ?? '');
          setDescricao(u.descricao ?? '');
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user?.id]);

  const handleFotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setFotoUrl(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id) return;
    setSaving(true);
    try {
      const res = await apiService.atualizarUsuario(user.id, {
        fotoUrl: fotoUrl || null,
        assinaturaUrl: assinaturaUrl || undefined,
        assinaturaDigital: assinaturaDigital || undefined,
        cargo: cargo.trim() || undefined,
        crea: crea.trim() || undefined,
        descricao: descricao.trim() || undefined,
      });
      if (res.success) {
        await refreshUser();
      } else {
        alert(res.message || t('revisoes.perfil.erroSalvar'));
      }
    } catch {
      alert(t('revisoes.perfil.erroSalvar'));
    } finally {
      setSaving(false);
    }
  };

  if (!user) {
    return (
      <div className="flex justify-center py-12">
        <p className="text-schmersal-gray-medium dark:text-schmersal-gray-light">{t('revisoes.perfil.semLogin')}</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-schmersal-blue" />
      </div>
    );
  }

  const fotoExibir = urlParaExibicaoFoto(fotoUrl);

  return (
    <div className="max-w-2xl space-y-6">
      <h1 className="text-2xl font-bold text-schmersal-gray-dark dark:text-white flex items-center gap-2">
        <User className="h-7 w-7" />
        {t('revisoes.perfil.titulo')}
      </h1>

      <form onSubmit={handleSubmit} className="space-y-6 bg-white dark:bg-schmersal-gray-darker rounded-lg shadow p-6">
        <div>
          <label className="block text-sm font-medium text-schmersal-gray-dark dark:text-white mb-2">{t('revisoes.perfil.fotoRotulo')}</label>
          <div className="flex items-start gap-4">
            <div className="w-24 h-24 rounded-full overflow-hidden bg-schmersal-gray-light dark:bg-schmersal-gray-dark flex items-center justify-center border border-rg-line dark:border-schmersal-gray-dark">
              {fotoExibir ? (
                <img src={fotoExibir} alt={t('revisoes.perfil.fotoAlt')} className="w-full h-full object-cover" />
              ) : (
                <Camera className="h-10 w-10 text-schmersal-gray-medium dark:text-schmersal-gray-light" />
              )}
            </div>
            <div>
              <input
                type="file"
                accept="image/*"
                onChange={handleFotoChange}
                className="block w-full text-sm text-schmersal-gray-dark dark:text-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-schmersal-blue file:text-white file:font-medium hover:file:bg-schmersal-blue-dark"
              />
              <p className="text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light mt-1">
                {t('revisoes.perfil.fotoDica')}
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="col-span-1 md:col-span-2">
            <label className="block text-sm font-medium text-schmersal-gray-dark dark:text-white mb-1">{t('revisoes.perfil.creaRotulo')}</label>
            <input
              type="text"
              value={crea}
              onChange={(e) => setCrea(e.target.value)}
              placeholder={t('revisoes.perfil.creaPlaceholder')}
              className="w-full px-3 py-2 border border-schmersal-gray-light dark:border-schmersal-gray-dark rounded-lg bg-white dark:bg-schmersal-gray-dark text-schmersal-gray-dark dark:text-white"
            />
          </div>

          <div className="col-span-1 md:col-span-2">
            <label className="block text-sm font-medium text-schmersal-gray-dark dark:text-white mb-1">{t('revisoes.perfil.cargoRotulo')}</label>
            <input
              type="text"
              value={cargo}
              onChange={(e) => setCargo(e.target.value)}
              placeholder={t('revisoes.perfil.cargoPlaceholder')}
              className="w-full px-3 py-2 border border-schmersal-gray-light dark:border-schmersal-gray-dark rounded-lg bg-white dark:bg-schmersal-gray-dark text-schmersal-gray-dark dark:text-white"
            />
          </div>

          <div className="col-span-1 md:col-span-2">
            <label className="block text-sm font-medium text-schmersal-gray-dark dark:text-white mb-1">{t('revisoes.perfil.descricaoRotulo')}</label>
            <textarea
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              placeholder={t('revisoes.perfil.descricaoPlaceholder')}
              className="w-full px-3 py-2 border border-schmersal-gray-light dark:border-schmersal-gray-dark rounded-lg bg-white dark:bg-schmersal-gray-dark text-schmersal-gray-dark dark:text-white h-24 whitespace-pre-wrap"
            />
            <p className="text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light mt-1">
              {t('revisoes.perfil.descricaoDica')}
            </p>
          </div>
        </div>

        <div>
          <AssinaturaManager
            assinaturaUrl={assinaturaUrl}
            assinaturaDigital={assinaturaDigital}
            onChange={(url, digital) => {
              setAssinaturaUrl(url);
              setAssinaturaDigital(digital);
            }}
            modoEdicao
            label={t('revisoes.perfil.assinaturaRotulo')}
          />
          <p className="text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light mt-1">
            {t('revisoes.perfil.assinaturaDica')}
          </p>
        </div>

        <div className="flex gap-3 pt-4 border-t border-schmersal-gray-light dark:border-schmersal-gray-dark">
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 bg-schmersal-blue text-white rounded-lg hover:bg-schmersal-blue-dark disabled:opacity-50 flex items-center gap-2"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {t('revisoes.perfil.acaoSalvar')}
          </button>
        </div>
      </form>
    </div>
  );
}
